import { Injectable, signal, computed, effect, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

/** The two density preset choices. */
export type DensityPreference = 'comfortable' | 'compact';

const STORAGE_KEY = 'tb-density-preference';
const DATA_ATTR   = 'data-density';

/**
 * DensityService — signal-based comfortable/compact density toggle (DESIGN.md §5/§7.1).
 *
 * - Reads preference from localStorage on construction; falls back to provided default.
 * - Sets `data-density="compact"` (or clears the attribute for comfortable) on `<html>`
 *   so CSS @mixin tokens-compact applies.
 * - The `preference` signal can be toggled or set directly.
 * - Provided in root; inject anywhere.
 *
 * Default differs per app:
 *   Customer SPA    → 'comfortable' (spacious reading surface)
 *   Back-Office     → 'compact'     (dense ops work — more rows per screen)
 */
@Injectable({ providedIn: 'root' })
export class DensityService {
  private readonly doc = inject(DOCUMENT);

  /** User's stored/active density preference. */
  readonly preference = signal<DensityPreference>(this._readStored('comfortable'));

  /** True when compact mode is in effect. */
  readonly isCompact = computed(() => this.preference() === 'compact');

  constructor() {
    // Apply / remove data-density attribute whenever preference changes.
    effect(() => {
      const density = this.preference();
      if (density === 'compact') {
        this.doc.documentElement.setAttribute(DATA_ATTR, 'compact');
      } else {
        this.doc.documentElement.removeAttribute(DATA_ATTR);
      }
    });
  }

  /** Toggle between comfortable and compact. */
  toggle(): void {
    this.set(this.preference() === 'compact' ? 'comfortable' : 'compact');
  }

  set(density: DensityPreference): void {
    this.preference.set(density);
    try {
      localStorage.setItem(STORAGE_KEY, density);
    } catch {
      // Private-browsing / storage-quota — silently ignore.
    }
  }

  /**
   * Bootstrap with an app-default.
   * Call once at app startup (e.g. in APP_INITIALIZER or the root component constructor)
   * to override the 'comfortable' fallback for apps that default to compact.
   * localStorage wins over the app-default if the user has explicitly set a preference.
   */
  initDefault(appDefault: DensityPreference): void {
    const stored = this._readStored(appDefault);
    // Only apply the default if no stored preference exists
    if (!this._hasStored()) {
      this.set(appDefault);
    } else {
      this.preference.set(stored);
    }
  }

  private _hasStored(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) !== null;
    } catch {
      return false;
    }
  }

  private _readStored(fallback: DensityPreference): DensityPreference {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === 'comfortable' || v === 'compact') return v;
    } catch { /* localStorage unavailable */ }
    return fallback;
  }
}
